import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { AMENITY_ALIASES } from '../constants';
import SovereigntyMarkers from '../components/SovereigntyMarkers';
import { useHousingData } from '../hooks/useHousingData';
import { useSavedProperties } from '../hooks/useSavedProperties';
import { fetchMapData } from '../services/mapService';
import { submitUserBooking } from '../services/userBookingService';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Normalize amenity key: lowercase, strip spaces, replace hyphens/underscores with nothing
const normalizeAmenity = (key) =>
  key
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
    .trim();

const amenityLabels = {};
// Build the lookup from centralized AMENITY_ALIASES in constants.js
for (const [canonicalKey, aliases] of Object.entries(AMENITY_ALIASES)) {
  for (const alias of aliases) {
    amenityLabels[normalizeAmenity(alias)] = `amenity.${canonicalKey}`;
  }
}

// Custom marker icon for POI places (smaller teardrop pins)
const createPoiIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      position: relative;
      width: 28px; height: 28px;
    ">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2C8.48 2 4 6.48 4 12C4 19.25 14 26 14 26C14 26 24 19.25 24 12C24 6.48 19.52 2 14 2Z"
          fill="${color}" stroke="white" stroke-width="1.5"/>
        <circle cx="14" cy="12" r="4.5" fill="white"/>
      </svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
    popupAnchor: [0, -26],
  });
};

// Custom pinned icon for the property location (teardrop/pin shape)
const createPinIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      position: relative;
      width: 36px; height: 36px;
    ">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2C10.82 2 5 7.82 5 15C5 24.75 18 34 18 34C18 34 31 24.75 31 15C31 7.82 25.18 2 18 2Z"
          fill="${color}" stroke="white" stroke-width="2"/>
        <circle cx="18" cy="15" r="6" fill="white"/>
      </svg>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 34],
    popupAnchor: [0, -34],
  });
};

const iconColors = {
  university: '#3B82F6',
  hospital: '#EF4444',
  market: '#F59E0B',
  mall: '#8B5CF6',
  property: '#10B981',
};

const getIconForType = (type) => {
  if (type === 'property') {
    return createPinIcon(iconColors[type] || '#10B981');
  }
  return createPoiIcon(iconColors[type] || '#6B7280');
};

const HousingDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const videoRef = useRef(null);
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const mapFetchedRef = useRef(false);
  const contactRef = useRef(null);
  const amenitiesRef = useRef(null);
  const mapRef = useRef(null);

  const { getPropertyById, loading } = useHousingData();
  const { isSaved, toggleSaved } = useSavedProperties();
  const [housing, setHousing] = useState(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showCopyPhoneToast, setShowCopyPhoneToast] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    phone: '',
    visitingDate: '',
    note: '',
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState('');

  useEffect(() => {
    if (!loading) {
      const result = getPropertyById(id);
      setHousing(result);
    }
  }, [id, getPropertyById, loading]);

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(housing.contactPhone);
      setShowCopyPhoneToast(true);
      setTimeout(() => setShowCopyPhoneToast(false), 2500);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = housing.contactPhone;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopyPhoneToast(true);
      setTimeout(() => setShowCopyPhoneToast(false), 2500);
    }
  };

  const handleImageLoad = (index) => {
    setLoadedImages((prev) => new Set([...prev, index]));
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2500);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2500);
    }
  };

  // Dynamically fetch map data via Cloudflare proxy (Nominatim + Overpass)
  // Uses additionalAddresses for geocoding with failover, and displayAddress for UI
  useEffect(() => {
    if (!housing || mapFetchedRef.current) return;
    mapFetchedRef.current = true;

    let cancelled = false;

    const loadMapData = async () => {
      setMapLoading(true);
      setMapError(null);

      try {
        // Use displayAddress for UI display, but additionalAddresses for geocoding/map
        const displayAddress = housing.displayAddressVi || housing.displayAddress || housing.addressVi || housing.address || '';
        const district = housing.districtVi || housing.district || '';
        const city = housing.cityVi || housing.city || '';
        const additionalAddresses = housing.additionalAddresses || [];

        // Pass additionalAddresses to fetchMapData for geocoding with failover.
        // opts.encrypted=true requests encrypted /api/geocode responses (same
        // XOR + base64 pattern as /api/drive-all-properties) for this page.
        const result = await fetchMapData(displayAddress, district, city, additionalAddresses, { encrypted: true });

        if (!cancelled) {
          setMapData(result);
          if (!result.coordinates) {
            setMapError('Could not locate this property on the map.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[HousingDetail] Map data error:', err);
          setMapError('Failed to load map data.');
        }
      } finally {
        if (!cancelled) {
          setMapLoading(false);
        }
      }
    };

    loadMapData();

    return () => {
      cancelled = true;
    };
  }, [housing]);

  // Combine images and videos into a single media array with type info
  // Must be before any early returns (rules of hooks)
  const mediaItems = useMemo(() => {
    if (!housing) return [];
    const items = [];
    (housing.images || []).forEach((url) => {
      items.push({ url, type: 'image' });
    });
    (housing.videos || []).forEach((url) => {
      items.push({ url, type: 'video' });
    });
    return items;
  }, [housing?.images, housing?.videos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!housing) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h2>
        <p className="text-gray-500 mb-8">The property you're looking for doesn't exist or has been removed.</p>
        <Link to="/" className="btn-primary">{t('detail.backToList')}</Link>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(Math.round(price));
  };

  // Always show Vietnamese content first, fall back to English
  const title = housing.titleVi || housing.title;
  const description = housing.descriptionVi || housing.description;
  // Use displayAddress for display purposes (shows the named address, not the geocoding addresses)
  const displayAddress = housing.displayAddressVi || housing.displayAddress || housing.addressVi || housing.address;
  const districtName = housing.districtVi || housing.district;
  const cityName = housing.cityVi || housing.city;

  // Clean up description: convert literal \n to newlines, strip JS string concatenation artifacts
  const cleanDescription = (description || '').replace(/\\n/g, '\n').replace(/\s*'\s*\+\s*'\s*/g, '').replace(/^'\s*|\s*'$/g, '');

  return (
    <div className="min-h-screen">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-gray-500 hover:text-primary-600 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          <span>{t('detail.backToList')}</span>
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images, Description, Map */}
          <div className="lg:col-span-2 space-y-8">
            {/* Main Media Gallery (images + videos) */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="relative h-72 sm:h-96 lg:h-[420px] bg-gray-100">
                {mediaItems.length > 0 && mediaItems[selectedImage]?.type === 'video' ? (
                  <video
                    ref={videoRef}
                    src={mediaItems[selectedImage].url}
                    controls
                    className="w-full h-full object-contain bg-black"
                    onLoadedData={() => handleImageLoad(`main-${selectedImage}`)}
                    autoPlay={false}
                    playsInline
                  />
                ) : (
                  <>
                    {!loadedImages.has(`main-${selectedImage}`) && (
                      <div className="absolute inset-0 animate-pulse">
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <div className="w-12 h-12 border-4 border-gray-300 border-t-gray-400 rounded-full animate-spin" />
                        </div>
                      </div>
                    )}
                    {mediaItems.length > 0 && (
                      <img
                        src={mediaItems[selectedImage]?.url}
                        alt={`${title} - ${selectedImage + 1}`}
                        className={`w-full h-full object-contain transition-opacity duration-300 ${
                          loadedImages.has(`main-${selectedImage}`) ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => handleImageLoad(`main-${selectedImage}`)}
                      />
                    )}
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none" />
                {mediaItems.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImage((prev) => prev === 0 ? mediaItems.length - 1 : prev - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                    >
                      <svg className="w-5 h-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setSelectedImage((prev) => prev === mediaItems.length - 1 ? 0 : prev + 1)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                    >
                      <svg className="w-5 h-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-lg text-sm">
                  {selectedImage + 1} / {mediaItems.length}
                </div>
              </div>
              {mediaItems.length > 1 && (
                <div className="flex items-center gap-2 p-4 overflow-x-auto">
                  {mediaItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (item.type === 'video' && videoRef.current) {
                          videoRef.current.pause();
                        }
                        setSelectedImage(index);
                      }}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all relative ${
                        selectedImage === index
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {item.type === 'video' ? (
                        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      ) : (
                        <>
                          {!loadedImages.has(`thumb-${index}`) && (
                            <div className="absolute inset-0 animate-pulse bg-gray-200 rounded-lg" />
                          )}
                          <img
                            src={item.url}
                            alt={`Thumbnail ${index + 1}`}
                            className={`w-full h-full object-contain bg-gray-50 transition-opacity duration-300 ${
                              loadedImages.has(`thumb-${index}`) ? 'opacity-100' : 'opacity-0'
                            }`}
                            onLoad={() => handleImageLoad(`thumb-${index}`)}
                          />
                        </>
                      )}
                      {item.type === 'video' && (
                        <div className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1 rounded">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile: Property Title + Price (below images, above description) */}
            <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
              <div className="text-3xl font-bold text-primary-600 mb-4">
                {formatPrice(housing.price)}
                <span className="text-base font-normal text-gray-500"> {t('listing.pricePerMonth')}</span>
              </div>

              <div className="flex justify-around py-4 border-t border-gray-100">
                <button
                  onClick={() => amenitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="text-center group cursor-pointer"
                >
                  <div className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{housing.amenities.length}</div>
                  <div className="text-xs text-gray-500 mt-1 group-hover:text-primary-500 transition-colors">{t('detail.amenities')}</div>
                </button>
                <div className="text-center">
                  <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-bold ${
                    housing.available
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      housing.available ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <span>{housing.available ? t('detail.available') : t('detail.unavailable')}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t('detail.status')}</div>
                </div>
              </div>

              <button
                onClick={() => mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="py-4 border-t border-gray-100 w-full text-left group"
              >
                <div className="flex items-start space-x-2.5">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-500 group-hover:text-primary-500 transition-colors">{t('detail.address')}</p>
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors">{displayAddress}</p>
                    <p className="text-sm text-gray-600 group-hover:text-primary-500 transition-colors">{districtName}, {cityName}</p>
                  </div>
                </div>
              </button>

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <a
                  href={`tel:${housing.contactPhone}`}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{t('detail.contact')}</span>
                </a>
                <button
                  onClick={() => toggleSaved(housing.id)}
                  className={`px-4 py-2.5 rounded-lg border-2 font-medium transition-all duration-200 ${
                    isSaved(housing.id)
                      ? 'border-primary-500 bg-primary-50 text-primary-600'
                      : 'border-gray-200 text-gray-500 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <svg className={`w-5 h-5 ${isSaved(housing.id) ? 'fill-current' : ''}`} fill={isSaved(housing.id) ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile: Contact Info (below property card) */}
            <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-4">{t('detail.contactInfo')}</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('detail.phone')}</p>
                    <button
                      onClick={handleCopyPhone}
                      className="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors text-left"
                    >
                      {housing.contactPhone}
                    </button>
                  </div>
                </div>
                {/* Zalo Contact */}
                <a
                  href={`https://zalo.me/${housing.contactPhone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 group"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.49 10.04v.78h-.71v-.46a.35.35 0 0 0-.11-.27.37.37 0 0 0-.27-.11h-.52v2.06h.52a.37.37 0 0 0 .27-.11.35.35 0 0 0 .11-.27v-.46h.71v.78a.87.87 0 0 1-.26.6.87.87 0 0 1-.6.26h-.84a.87.87 0 0 1-.6-.26.87.87 0 0 1-.26-.6v-2.12a.87.87 0 0 1 .26-.6.87.87 0 0 1 .6-.26h.84a.87.87 0 0 1 .6.26.87.87 0 0 1 .26.6zM9.33 10.64a.37.37 0 0 0-.11-.27.37.37 0 0 0-.27-.11.37.37 0 0 0-.27.11.37.37 0 0 0-.11.27v1.36a.38.38 0 0 0 .11.27.37.37 0 0 0 .27.11.37.37 0 0 0 .27-.11.38.38 0 0 0 .11-.27v-1.36zm2.13.04v.78h-.71v-.46a.35.35 0 0 0-.11-.27.37.37 0 0 0-.27-.11h-.52v2.06h.52a.37.37 0 0 0 .27-.11.35.35 0 0 0 .11-.27v-.46h.71v.78a.87.87 0 0 1-.26.6.87.87 0 0 1-.6.26h-.84a.87.87 0 0 1-.6-.26.87.87 0 0 1-.26-.6v-2.12a.87.87 0 0 1 .26-.6.87.87 0 0 1 .6-.26h.84a.87.87 0 0 1 .6.26.87.87 0 0 1 .26.6zM7.42 12.27h-.73v-1.62H5.82v-.93h2.36v.93h-.76v1.62zm7.78-2.23h.95v2.96h-.95v-2.96zm1.9-1.31c.3 0 .55.25.55.55s-.25.55-.55.55-.55-.25-.55-.55.25-.55.55-.55zm0 .28c.15 0 .27.12.27.27s-.12.27-.27.27-.27-.12-.27-.27.12-.27.27-.27zm-.14.43h.12l.12.19h.13l-.14-.22a.18.18 0 0 0 .08-.07c.02-.04.03-.08.03-.13 0-.06-.02-.1-.05-.14a.2.2 0 0 0-.12-.06c-.02 0-.05 0-.08-.01h-.22v.65h.1v-.21zm.1-.11v-.2h.1c.03 0 .06 0 .08.01a.09.09 0 0 1 .06.05c.01.02.02.05.02.08 0 .03 0 .06-.02.08a.09.09 0 0 1-.06.05c-.02.01-.05.01-.08.01h-.1zm6.06.54a.82.82 0 0 1-.58.24.82.82 0 0 1-.58-.24.79.79 0 0 1-.24-.58v-.89h.32v.87c0 .15.05.27.16.37a.53.53 0 0 0 .76 0 .5.5 0 0 0 .16-.37v-.87h.32v.89a.79.79 0 0 1-.24.58z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">{t('detail.zalo')}</p>
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{housing.contactPhone}</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Mobile: Book a Visit - between contact info and share */}
            <button
              onClick={() => {
                setBookingForm({ name: '', phone: '', visitingDate: '', note: '' });
                setBookingSuccess(false);
                setBookingError('');
                setShowBookingModal(true);
              }}
              className="w-full lg:hidden btn-primary flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Đặt lịch xem nhà</span>
            </button>

            {/* Mobile: Share button */}
            <button onClick={handleShare} className="w-full lg:hidden btn-outline flex items-center justify-center space-x-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span>{t('detail.shareProperty')}</span>
            </button>

            {/* Description */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('detail.description')}</h2>
              <div className="text-gray-600 leading-relaxed whitespace-pre-wrap break-words">
                {cleanDescription}
              </div>
            </div>

            {/* Amenities */}
            <div ref={amenitiesRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5">{t('detail.amenities')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {housing.amenities.map((amenity) => {
                  const i18nKey = amenityLabels[normalizeAmenity(amenity)];
                  return (
                    <div
                      key={amenity}
                      className="flex items-center space-x-2.5 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100"
                    >
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {i18nKey ? t(i18nKey) : amenity.charAt(0).toUpperCase() + amenity.slice(1)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Embedded Map - Leaflet + OpenStreetMap via Cloudflare proxy */}
            <div ref={mapRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5">{t('map.title')}</h2>

              {mapLoading && (
                <div className="flex items-center justify-center h-[300px] bg-gray-50 rounded-xl">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-500">{t('common.loading')}</p>
                  </div>
                </div>
              )}

              {mapError && !mapLoading && (
                <div className="flex items-center justify-center h-[300px] bg-gray-50 rounded-xl">
                  <div className="text-center px-6">
                    <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-gray-500">{mapError}</p>
                  </div>
                </div>
              )}

              {!mapLoading && !mapError && mapData && mapData.coordinates && (
                <>
                  <div className="flex flex-wrap gap-4 mb-4 text-sm">
                    {[
                      { type: 'university', color: '#3B82F6' },
                      { type: 'hospital', color: '#EF4444' },
                      { type: 'market', color: '#F59E0B' },
                      { type: 'mall', color: '#8B5CF6' },
                    ].map((item) => (
                      <div key={item.type} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-600">{t(`map.${item.type}`)}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10B981' }} />
                      <span className="text-gray-600">{t('map.property')}</span>
                    </div>
                  </div>

                  <div className="h-[400px] rounded-xl overflow-hidden border border-gray-200">
                    <MapContainer
                      center={[mapData.coordinates.lat, mapData.coordinates.lng]}
                      zoom={14}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={false}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      {/* Hoàng Sa, Trường Sa sovereignty markers */}
                      <SovereigntyMarkers />

                      <Marker
                        position={[mapData.coordinates.lat, mapData.coordinates.lng]}
                        icon={getIconForType('property')}
                      >
                        <Popup>
                          <div className="text-sm font-medium">
                            <p className="font-bold">{title}</p>
                            <p className="text-primary-600 font-semibold mt-1">{formatPrice(housing.price)}</p>
                          </div>
                        </Popup>
                      </Marker>

                      {mapData.places.map((place, index) => (
                        <Marker
                          key={index}
                          position={[place.lat, place.lng]}
                          icon={getIconForType(place.type)}
                        >
                          <Popup>
                            <div className="text-sm">
                              <p className="font-semibold">{place.nameVi || place.name}</p>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>
                </>
              )}

              {!mapLoading && !mapError && mapData && !mapData.coordinates && (
                <div className="flex items-center justify-center h-[300px] bg-gray-50 rounded-xl">
                  <div className="text-center px-6">
                    <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <p className="text-sm text-gray-500">Không thể xác định vị trí trên bản đồ</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Desktop only */}
          <div className="hidden lg:block space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
              <div className="text-3xl font-bold text-primary-600 mb-4">
                {formatPrice(housing.price)}
                <span className="text-base font-normal text-gray-500"> {t('listing.pricePerMonth')}</span>
              </div>

              <div className="flex justify-around py-4 border-t border-gray-100">
                <button
                  onClick={() => amenitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="text-center group cursor-pointer"
                >
                  <div className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{housing.amenities.length}</div>
                  <div className="text-xs text-gray-500 mt-1 group-hover:text-primary-500 transition-colors">{t('detail.amenities')}</div>
                </button>
                <div className="text-center">
                  <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-bold ${
                    housing.available
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      housing.available ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <span>{housing.available ? t('detail.available') : t('detail.unavailable')}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t('detail.status')}</div>
                </div>
              </div>

              <button
                onClick={() => mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="py-4 border-t border-gray-100 w-full text-left group"
              >
                <div className="flex items-start space-x-2.5">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-500 group-hover:text-primary-500 transition-colors">{t('detail.address')}</p>
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors">{displayAddress}</p>
                    <p className="text-sm text-gray-600 group-hover:text-primary-500 transition-colors">{districtName}, {cityName}</p>
                  </div>
                </div>
              </button>

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <a
                  href={`tel:${housing.contactPhone}`}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{t('detail.contact')}</span>
                </a>
                <button
                  onClick={() => toggleSaved(housing.id)}
                  className={`px-4 py-2.5 rounded-lg border-2 font-medium transition-all duration-200 ${
                    isSaved(housing.id)
                      ? 'border-primary-500 bg-primary-50 text-primary-600'
                      : 'border-gray-200 text-gray-500 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <svg className={`w-5 h-5 ${isSaved(housing.id) ? 'fill-current' : ''}`} fill={isSaved(housing.id) ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
              </div>
            </div>

          <div ref={contactRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-4">{t('detail.contactInfo')}</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('detail.phone')}</p>
                    <button
                      onClick={handleCopyPhone}
                      className="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors text-left"
                    >
                      {housing.contactPhone}
                    </button>
                  </div>
                </div>
                {/* Zalo Contact */}
                <a
                  href={`https://zalo.me/${housing.contactPhone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 group"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.49 10.04v.78h-.71v-.46a.35.35 0 0 0-.11-.27.37.37 0 0 0-.27-.11h-.52v2.06h.52a.37.37 0 0 0 .27-.11.35.35 0 0 0 .11-.27v-.46h.71v.78a.87.87 0 0 1-.26.6.87.87 0 0 1-.6.26h-.84a.87.87 0 0 1-.6-.26.87.87 0 0 1-.26-.6v-2.12a.87.87 0 0 1 .26-.6.87.87 0 0 1 .6-.26h.84a.87.87 0 0 1 .6.26.87.87 0 0 1 .26.6zM9.33 10.64a.37.37 0 0 0-.11-.27.37.37 0 0 0-.27-.11.37.37 0 0 0-.27.11.37.37 0 0 0-.11.27v1.36a.38.38 0 0 0 .11.27.37.37 0 0 0 .27.11.37.37 0 0 0 .27-.11.38.38 0 0 0 .11-.27v-1.36zm2.13.04v.78h-.71v-.46a.35.35 0 0 0-.11-.27.37.37 0 0 0-.27-.11h-.52v2.06h.52a.37.37 0 0 0 .27-.11.35.35 0 0 0 .11-.27v-.46h.71v.78a.87.87 0 0 1-.26.6.87.87 0 0 1-.6.26h-.84a.87.87 0 0 1-.6-.26.87.87 0 0 1-.26-.6v-2.12a.87.87 0 0 1 .26-.6.87.87 0 0 1 .6-.26h.84a.87.87 0 0 1 .6.26.87.87 0 0 1 .26.6zM7.42 12.27h-.73v-1.62H5.82v-.93h2.36v.93h-.76v1.62zm7.78-2.23h.95v2.96h-.95v-2.96zm1.9-1.31c.3 0 .55.25.55.55s-.25.55-.55.55-.55-.25-.55-.55.25-.55.55-.55zm0 .28c.15 0 .27.12.27.27s-.12.27-.27.27-.27-.12-.27-.27.12-.27.27-.27zm-.14.43h.12l.12.19h.13l-.14-.22a.18.18 0 0 0 .08-.07c.02-.04.03-.08.03-.13 0-.06-.02-.1-.05-.14a.2.2 0 0 0-.12-.06c-.02 0-.05 0-.08-.01h-.22v.65h.1v-.21zm.1-.11v-.2h.1c.03 0 .06 0 .08.01a.09.09 0 0 1 .06.05c.01.02.02.05.02.08 0 .03 0 .06-.02.08a.09.09 0 0 1-.06.05c-.02.01-.05.01-.08.01h-.1zm6.06.54a.82.82 0 0 1-.58.24.82.82 0 0 1-.58-.24.79.79 0 0 1-.24-.58v-.89h.32v.87c0 .15.05.27.16.37a.53.53 0 0 0 .76 0 .5.5 0 0 0 .16-.37v-.87h.32v.89a.79.79 0 0 1-.24.58z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">{t('detail.zalo')}</p>
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{housing.contactPhone}</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Book a Visit Button - Desktop (between contact info and share) */}
            <button
              onClick={() => {
                setBookingForm({ name: '', phone: '', visitingDate: '', note: '' });
                setBookingSuccess(false);
                setBookingError('');
                setShowBookingModal(true);
              }}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Đặt lịch xem nhà</span>
            </button>

            <button onClick={handleShare} className="w-full btn-outline flex items-center justify-center space-x-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span>{t('detail.shareProperty')}</span>
            </button>

          </div>
        </div>
      </div>

      {/* Copy URL notification toast — outside grid for full-screen visibility */}
      {showCopyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] animate-bounce">
          <div className="bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center space-x-2 whitespace-nowrap">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{t('detail.copied')}</span>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-lg font-bold text-gray-900">Đặt lịch xem nhà</h3>
              <button
                onClick={() => setShowBookingModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {bookingSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Đăng ký thành công!</h4>
                <p className="text-gray-600 mb-6">Chúng tôi sẽ liên hệ với bạn để xác nhận lịch hẹn xem nhà.</p>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Đóng
                </button>
              </div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setBookingError('');
                  setBookingSubmitting(true);
                  try {
                    await submitUserBooking({
                      ...bookingForm,
                      propertyUrl: window.location.href,
                      propertyName: title,
                      propertyId: housing.id || '',
                      district: districtName || '',
                    });
                    setBookingSuccess(true);
                  } catch (err) {
                    setBookingError(err.message);
                  } finally {
                    setBookingSubmitting(false);
                  }
                }}
                className="p-6 space-y-5"
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Họ tên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Nhập họ tên của bạn"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Số điện thoại <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="Nhập số điện thoại của bạn"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ngày & giờ xem nhà <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={bookingForm.visitingDate}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, visitingDate: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ghi chú
                  </label>
                  <textarea
                    value={bookingForm.note}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, note: e.target.value }))}
                    placeholder="Ghi chú thêm (không bắt buộc)..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors resize-none"
                  />
                </div>

                {bookingError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    {bookingError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={bookingSubmitting}
                  className="w-full py-3.5 bg-primary-600 text-white rounded-xl font-bold text-lg hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {bookingSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Đang gửi...
                    </>
                  ) : (
                    'Gửi đăng ký'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Copy phone notification toast */}
      {showCopyPhoneToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] animate-bounce">
          <div className="bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center space-x-2 whitespace-nowrap">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{t('detail.copiedPhone')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default HousingDetail;